import React, { useState, useEffect, useCallback } from 'react';
import API_BASE_URL from '../../../components/apiconfig/api-config';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import SearchableDropdown from '../../../components/common/SearchableDropdown';
import { MdWarning } from 'react-icons/md';
const DeliveryOrderForm = () => {
  const [formData, setFormData] = useState({
    do_date: '',
    issuing_authority_id: '',
    do_number: '',
    warehouse_id: '',
    warehouse_location: '',
    buyer_id: '', // Added buyer_id field
    buyer_name: '',
    commodity_id: '',
    quantity_mt: '',
    cha_cma_id: '', // Single field for both CMA and CHA IDs
    check_cma_cha: 'CMA', // Track whether it's CMA or CHA
    sales_contract_id: '' // Changed from sale_id to sales_contract_id
  });
  
  const [buyers, setBuyers] = useState([]);
  const [selectedSalesContract, setSelectedSalesContract] = useState(null); // Changed from selectedSaleOrder
  const [quantityError, setQuantityError] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [commodities, setCommodities] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [cmas, setCmas] = useState([]);
  const [chas, setChas] = useState([]);
  const [chaWarehouses, setChaWarehouses] = useState([]);
  const [selectedType, setSelectedType] = useState('CMA');
  const [issuingAuthorities, setIssuingAuthorities] = useState([]);
  const [salesContracts, setSalesContracts] = useState([]); // Changed from saleOrders
  const [warehouseQuantity, setWarehouseQuantity] = useState(0);
  const [warehouseQuantityMessage, setWarehouseQuantityMessage] = useState('');
  const [isContractFullyUtilized, setIsContractFullyUtilized] = useState(false);
  const [buyerBalance, setBuyerBalance] = useState(null);
  const [buyerBalanceLoading, setBuyerBalanceLoading] = useState(false);
  const [balanceExceededError, setBalanceExceededError] = useState('');
  const [valueOfUndeliveredMaterial, setValueOfUndeliveredMaterial] = useState(null);
  const [valueOfUndeliveredMaterialLoading, setValueOfUndeliveredMaterialLoading] = useState(false);
  const [valueOfEmdLessThan90, setValueOfEmdLessThan90] = useState(null);
  const [valueOfEmdLessThan90Loading, setValueOfEmdLessThan90Loading] = useState(false);
  const [emdLessThan90Details, setEmdLessThan90Details] = useState([]);
  const [showEmdDetails, setShowEmdDetails] = useState(false);
  const [unapprovedReceivedAmount, setUnapprovedReceivedAmount] = useState(null);
  const [unapprovedReceivedAmountLoading, setUnapprovedReceivedAmountLoading] = useState(false);
  const [calculatedBuyersBalance, setCalculatedBuyersBalance] = useState(null);
  const [buyersBalanceError, setBuyersBalanceError] = useState('');
  const [emdStatus, setEmdStatus] = useState({ checking: false, allowed: true, message: '' });
  const [contractClosurePopup, setContractClosurePopup] = useState({ open: false, message: '' });
  const navigate = useNavigate();

  const formatDisplayDate = (dateValue) => {
    if (!dateValue) return '';
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return dateValue;
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const buildClosedContractMessage = (salesContract) => {
    const contractNumber = salesContract?.sales_contract_no || 'the selected sales contract';
    const closingDate = formatDisplayDate(salesContract?.closing_date);

    return closingDate
      ? `Delivery order cannot be created because sales contract ${contractNumber} was closed on ${closingDate}.`
      : `Delivery order cannot be created because sales contract ${contractNumber} is already closed.`;
  };

  // Fetch dropdown data
  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        // Fetch commodities
        const commodityRes = await fetch(`${API_BASE_URL}/api/masters/dropdown-commodities`, {
          credentials: 'include'
        });
        if (commodityRes.ok) {
          const commodityData = await commodityRes.json();
          setCommodities(commodityData.data || []);
        }
        
        // Fetch CMAs
        const cmaRes = await fetch(`${API_BASE_URL}/api/masters/dropdown-cma-list`, {
          credentials: 'include'
        });
        if (cmaRes.ok) {
          const cmaData = await cmaRes.json();
          setCmas(cmaData.data || []);
        }
        
        // Fetch CHAs
        const chaRes = await fetch(`${API_BASE_URL}/api/masters/dropdown-cha-list`, {
          credentials: 'include'
        });
        if (chaRes.ok) {
          const chaData = await chaRes.json();
          setChas(chaData.data || []);
        }
        
        // Fetch Issuing Authorities
        const authorityRes = await fetch(`${API_BASE_URL}/api/masters/dropdown-issuing-authorities`, {
          credentials: 'include'
        });
        if (authorityRes.ok) {
          const authorityData = await authorityRes.json();
          setIssuingAuthorities(authorityData.data || []);
        }
        
        // Fetch Sales Contracts (changed from Sale Orders)
        const salesContractRes = await fetch(`${API_BASE_URL}/api/masters/dropdown-sales-contracts`, {
          credentials: 'include'
        });
        if (salesContractRes.ok) {
          const salesContractData = await salesContractRes.json();
          setSalesContracts(salesContractData.data || []);
        }
        
        // Fetch Buyers
        const buyerRes = await fetch(`${API_BASE_URL}/api/masters/dropdown-domestic-buyers`, {
          credentials: 'include'
        });
        if (buyerRes.ok) {
          const buyerData = await buyerRes.json();
          setBuyers(buyerData.data || []);
        }
      } catch (error) {
        console.error('Error fetching dropdown data:', error);
      }
    };
    fetchDropdownData();
  }, []);

  // Check if contract is fully utilized
  useEffect(() => {
    if (selectedSalesContract) {
      const contractQty = parseFloat(selectedSalesContract.contract_quantity_mt || 0);
      const usedQty = parseFloat(selectedSalesContract.used_quantity_mt || 0);
      const isFullyUtilized = contractQty === usedQty && contractQty > 0;
      setIsContractFullyUtilized(isFullyUtilized);
    } else {
      setIsContractFullyUtilized(false);
    }
  }, [selectedSalesContract]);

 // Function to fetch used quantity from delivery orders
const fetchUsedQuantityFromDeliveryOrders = async (salesContractId) => {
  if (!salesContractId) return 0;
  
  try {
    const res = await fetch(`${API_BASE_URL}/api/masters/dropdown-do-used-quantity?sales_contract_id=${salesContractId}`, {
      credentials: 'include'
    });
    if (res.ok) {
      const data = await res.json();
      return parseFloat(data.used_quantity || 0);
    }
  } catch (error) {
    console.error('Error fetching used quantity:', error);
  }
  return 0;
};

// Function to fetch buyer balance from party ledger
const fetchBuyerBalance = async (buyerId) => {
  if (!buyerId) {
    setBuyerBalance(null);
    setBalanceExceededError('');
    return;
  }
  
  setBuyerBalanceLoading(true);
  try {
    const params = new URLSearchParams({
      buyer_id: buyerId,
      calculate_balance_unfiltered: 'true'
    });
    
    const res = await fetch(`${API_BASE_URL}/api/report/party-ledger?${params}`, {
      credentials: 'include'
    });
    
    if (res.ok) {
      const data = await res.json();
      if (data.success && data.data && data.data.summary) {
        const balance = parseFloat(data.data.summary.final_balance || 0);
        setBuyerBalance(balance);
        
        // Validate balance after fetching if quantity and price are available
        if (formData.quantity_mt && selectedSalesContract?.sale_price_per_mt) {
          validateBalanceExceeded(formData.quantity_mt, selectedSalesContract);
        }
      } else {
        setBuyerBalance(0);
        if (formData.quantity_mt && selectedSalesContract?.sale_price_per_mt) {
          validateBalanceExceeded(formData.quantity_mt, selectedSalesContract);
        }
      }
    } else {
      setBuyerBalance(0);
      if (formData.quantity_mt && selectedSalesContract?.sale_price_per_mt) {
        validateBalanceExceeded(formData.quantity_mt, selectedSalesContract);
      }
    }
  } catch (error) {
    console.error('Error fetching buyer balance:', error);
    setBuyerBalance(null);
    setBalanceExceededError('');
  } finally {
    setBuyerBalanceLoading(false);
  }
};

// Function to fetch value of undelivered material based on buyer_id
// Formula: For each DO of the buyer:
//   - DO qty = quantity_mt from delivery_order
//   - SO qty = SUM(quantity_mt) from sales_order where delivery_order_id = do.do_id
//   - Balance qty = DO qty - SO qty
//   - Rate = sale_price_per_mt from sales_order (first one) or sales_contract if no sales_order
//   - Value = Balance qty * Rate
// Total = Sum of all values
const fetchValueOfUndeliveredMaterial = async (buyerId) => {
  if (!buyerId) {
    setValueOfUndeliveredMaterial(null);
    return;
  }
  
  setValueOfUndeliveredMaterialLoading(true);
  try {
    const params = new URLSearchParams({
      buyer_id: buyerId
    });
    
    const res = await fetch(`${API_BASE_URL}/api/masters/value-of-undelivered-material-by-buyer?${params}`, {
      credentials: 'include'
    });
    
    if (res.ok) {
      const data = await res.json();
      if (data.success && data.value_of_undelivered_material !== undefined) {
        const valueOfUndelivered = parseFloat(data.value_of_undelivered_material || 0);
        setValueOfUndeliveredMaterial(valueOfUndelivered);
      } else {
        setValueOfUndeliveredMaterial(0);
      }
    } else {
      setValueOfUndeliveredMaterial(null);
    }
  } catch (error) {
    console.error('Error fetching value of undelivered material:', error);
    setValueOfUndeliveredMaterial(null);
  } finally {
    setValueOfUndeliveredMaterialLoading(false);
  }
};

// Function to fetch EMD value of contracts executed less than 90%
const fetchEmdLessThan90Value = async (buyerId) => {
  if (!buyerId) {
    setValueOfEmdLessThan90(null);
    setEmdLessThan90Details([]);
    setShowEmdDetails(false);
    return;
  }
  
  setValueOfEmdLessThan90Loading(true);
  try {
    const params = new URLSearchParams({
      buyer_id: buyerId
    });
    
    const res = await fetch(`${API_BASE_URL}/api/masters/emd-less-than-90-value-by-buyer?${params}`, {
      credentials: 'include'
    });
    
    if (res.ok) {
      const data = await res.json();
      if (data.success && data.value !== undefined) {
        setValueOfEmdLessThan90(parseFloat(data.value || 0));
        setEmdLessThan90Details(data.details || []);
      } else {
        setValueOfEmdLessThan90(0);
        setEmdLessThan90Details([]);
      }
    } else {
      setValueOfEmdLessThan90(null);
      setEmdLessThan90Details([]);
    }
  } catch (error) {
    console.error('Error fetching EMD less than 90% value:', error);
    setValueOfEmdLessThan90(null);
    setEmdLessThan90Details([]);
  } finally {
    setValueOfEmdLessThan90Loading(false);
  }
};

// Function to fetch unapproved received amount from received payments API
// Buyer-wise calculation: Pick all payments of the selected buyer where status = "In Process"
// Ignore sales contract number completely
const fetchUnapprovedReceivedAmount = async (buyerId) => {
  if (!buyerId) {
    setUnapprovedReceivedAmount(null);
    return;
  }
  
  setUnapprovedReceivedAmountLoading(true);
  try {
    // Fetch all received payments - we'll filter by buyer_id on client side
    const params = new URLSearchParams({
      page: '1',
      limit: '1000' // Fetch all records to calculate total
    });
    
    const res = await fetch(`${API_BASE_URL}/api/masters/received-payment-get?${params}`, {
      credentials: 'include'
    });
    
    if (res.ok) {
      const data = await res.json();
      if (data.data && Array.isArray(data.data)) {
        // Filter for payments of the selected buyer with status 'In Process' only
        // Ignore sales contract completely - only filter by buyer_id and status
        const unapprovedPayments = data.data.filter(
          payment => payment.buyer_id === parseInt(buyerId) && 
                     payment.status && 
                     payment.status.toLowerCase() === 'in process'
        );
        
        // Sum up the amount_received for unapproved payments
        const totalUnapproved = unapprovedPayments.reduce((sum, payment) => {
          return sum + parseFloat(payment.amount_received || 0);
        }, 0);
        
        setUnapprovedReceivedAmount(totalUnapproved);
      } else {
        setUnapprovedReceivedAmount(0);
      }
    } else {
      setUnapprovedReceivedAmount(null);
    }
  } catch (error) {
    console.error('Error fetching unapproved received amount:', error);
    setUnapprovedReceivedAmount(null);
  } finally {
    setUnapprovedReceivedAmountLoading(false);
  }
};

// Function to calculate Buyer's Balance
// Formula: (Ledger Balance + Unapproved Received Amount) - Value of Undelivered Material
// Note: Current quantity is NOT included in the balance calculation - it should remain constant
const calculateBuyersBalance = useCallback(() => {
  // Check if all required values are available
  if (buyerBalance === null || unapprovedReceivedAmount === null || valueOfUndeliveredMaterial === null || valueOfEmdLessThan90 === null) {
    setCalculatedBuyersBalance(null);
    setBuyersBalanceError('');
    return;
  }

  const ledgerBalance = parseFloat(buyerBalance || 0);
  const unapprovedAmount = parseFloat(unapprovedReceivedAmount || 0);
  const valueOfUndelivered = parseFloat(valueOfUndeliveredMaterial || 0);
  const emdLessThan90 = parseFloat(valueOfEmdLessThan90 || 0);

  // Calculate base buyer's balance WITHOUT current quantity
  // Formula: (Ledger Balance + Unapproved Received Amount) - (Value of Undelivered Material + Value of EMD of Contracts executed less than 90%)
  const buyersBalance = (ledgerBalance + unapprovedAmount) - (valueOfUndelivered + emdLessThan90);

  setCalculatedBuyersBalance(buyersBalance);

  // Validate if balance would be negative AFTER accounting for current quantity
  // This is for validation only, not for display
  const quantity = parseFloat(formData.quantity_mt || 0);
  const salePricePerMt = parseFloat(selectedSalesContract?.sale_price_per_mt || 0);
  const quantityValue = quantity * salePricePerMt;
  const balanceAfterQuantity = buyersBalance - quantityValue;

  if (quantity > 0 && balanceAfterQuantity < 0) {
    setBuyersBalanceError('Buyer\'s Balance would be negative after this delivery order. Cannot create delivery order.');
  } else {
    setBuyersBalanceError('');
  }
}, [buyerBalance, unapprovedReceivedAmount, valueOfUndeliveredMaterial, valueOfEmdLessThan90, formData.quantity_mt, selectedSalesContract?.sale_price_per_mt]);

const fetchEmdValidation = useCallback(async (salesContractId) => {
  if (!salesContractId) {
    setEmdStatus({ checking: false, allowed: true, message: '' });
    return;
  }
  setEmdStatus({ checking: true, allowed: true, message: '' });
  try {
    const params = new URLSearchParams({ sales_contract_id: salesContractId }); // ✅ NEW
    const res = await fetch(`${API_BASE_URL}/api/check-emd-status?${params}`, {
      credentials: 'include'
    });
    const data = await res.json();
    if (res.ok && data.success) {
      setEmdStatus({
        checking: false,
        allowed: data.allowed,
        message: data.allowed ? '' : data.message || 'Party is having pending EMD.'
      });
    } else {
      setEmdStatus({ checking: false, allowed: true, message: '' });
    }
  } catch (error) {
    console.error('Error fetching EMD status:', error);
    setEmdStatus({ checking: false, allowed: true, message: '' });
  }
}, []);

const isEmdBlocked = emdStatus.checking || !emdStatus.allowed;

// Calculate Buyer's Balance whenever relevant values change
useEffect(() => {
  calculateBuyersBalance();
}, [calculateBuyersBalance]);

useEffect(() => {
  fetchEmdValidation(formData.sales_contract_id);  // ✅ NEW
}, [formData.sales_contract_id, fetchEmdValidation]);

// Function to validate warehouse quantity
const validateWarehouseQuantity = async (warehouseId, commodityId, salesContractId) => {
  if (!warehouseId || !commodityId || !salesContractId) {
    setWarehouseQuantity(0);
    setWarehouseQuantityMessage('');
    return;
  }
  
  try {
    // Show loading message immediately
    setWarehouseQuantityMessage('🔄 Checking warehouse quantity...');
    
    // First get the origin_order_id from sales contract
    const salesContractRes = await fetch(`${API_BASE_URL}/api/masters/sales-contract/${salesContractId}`, {
      credentials: 'include'
    });
    
    if (!salesContractRes.ok) {
      setWarehouseQuantity(0);
      setWarehouseQuantityMessage('❌ Error fetching sales contract details');
      return;
    }
    
    const salesContractData = await salesContractRes.json();
    const originOrderId = salesContractData.data?.order_id; // Changed from origin_order_id to order_id
    
    if (!originOrderId) {
      setWarehouseQuantity(0);
      setWarehouseQuantityMessage('❌ No order ID found for this sales contract');
      return;
    }
    
    console.log('Validating warehouse quantity:', {
      warehouseId,
      commodityId,
      originOrderId,
      salesContractId
    });
    
    // Now check warehouse quantity using the existing API
    const warehouseRes = await fetch(`${API_BASE_URL}/api/masters/warehouse-entry-by-import-order?order_id=${originOrderId}&warehouse_id=${warehouseId}`, {
      credentials: 'include'
    });
    
    const warehouseData = await warehouseRes.json();
    console.log('Warehouse API Response:', { status: warehouseRes.status, data: warehouseData });
    
    if (warehouseRes.ok && warehouseData.data) {
      const availableQty = parseFloat(warehouseData.data.available_qty_mt || 0);
      const receivedQty = parseFloat(warehouseData.data.received_qty_mt || 0);
      const issuedQty = parseFloat(warehouseData.data.issued_qty_mt || 0);
      const transferredQty = parseFloat(warehouseData.data.transferred_qty_mt || 0);
      const netSanQty = parseFloat(warehouseData.data.net_san_qty || 0);
      
      setWarehouseQuantity(availableQty);
      
      console.log('Warehouse quantity breakdown:', {
        receivedQty,
        issuedQty,
        transferredQty,
        netSanQty,
        availableQty
      });
      
      if (availableQty <= 0) {
        setWarehouseQuantityMessage(`⚠️ No quantity available in selected warehouse. ${netSanQty !== 0 ? `(Includes SAN Adj: ${netSanQty > 0 ? '+' : ''}${netSanQty} MT). ` : ''}Please create a warehouse entry first.`);
      } else {
        const sanText = netSanQty !== 0 ? ` | SAN Adj: ${netSanQty > 0 ? '+' : ''}${netSanQty.toLocaleString('en-IN', {
          minimumFractionDigits: 3,
          maximumFractionDigits: 3
        })} MT` : '';
        
        setWarehouseQuantityMessage(`✅ Available quantity: ${availableQty.toLocaleString('en-IN', {
          minimumFractionDigits: 3,
          maximumFractionDigits: 3
        })} MT (Received: ${receivedQty.toLocaleString('en-IN', {
          minimumFractionDigits: 3,
          maximumFractionDigits: 3
        })} MT)${sanText}`);
      }
    } else {
      setWarehouseQuantity(0);
      const errorMsg = warehouseData.message || warehouseData.error || 'No stock available';
      setWarehouseQuantityMessage(`⚠️ ${errorMsg} in selected warehouse. Please create a warehouse entry first before creating delivery order.`);
      console.log('Warehouse validation failed:', warehouseData);
    }
  } catch (error) {
    console.error('Error validating warehouse quantity:', error);
    setWarehouseQuantity(0);
    setWarehouseQuantityMessage('❌ Error checking warehouse quantity');
  }
};

// Updated function to fetch sales contract details
const fetchSalesContractDetails = async (salesContractId) => {
  if (!salesContractId) {
    setSelectedSalesContract(null);
    setQuantityError('');
      setFormData(prev => ({ 
        ...prev, 
        commodity_id: '',
        buyer_id: '',
        buyer_name: ''
      })); // Clear commodity and buyer when no contract selected
      setBuyerBalance(null); // Clear buyer balance when no contract selected
      setBalanceExceededError(''); // Clear balance error
      setValueOfUndeliveredMaterial(null); // Clear value of undelivered material
      setValueOfEmdLessThan90(null); // Clear EMD less than 90 value
      setEmdLessThan90Details([]);
      setShowEmdDetails(false);
      setUnapprovedReceivedAmount(null); // Clear unapproved received amount
      setCalculatedBuyersBalance(null); // Clear calculated buyer's balance
      setBuyersBalanceError(''); // Clear buyer's balance error
      setContractClosurePopup({ open: false, message: '' });
    return;
  }
  
  try {
    // Fetch sales contract details (existing API)
    const res = await fetch(`${API_BASE_URL}/api/masters/sales-contract/${salesContractId}`, {
      credentials: 'include'
    });
    
    if (res.ok) {
      const data = await res.json();
      const salesContract = data.data;
      
      // Fetch used quantity from delivery orders
      const usedQuantity = await fetchUsedQuantityFromDeliveryOrders(salesContractId);
      
      // Calculate remaining quantity
      const contractQuantity = parseFloat(salesContract.contract_quantity_mt || 0);
      const remainingQuantity = contractQuantity - usedQuantity;
      
      // Add calculated values to contract object
      const enhancedContract = {
        ...salesContract,
        used_quantity_mt: usedQuantity,
        remaining_quantity_mt: remainingQuantity
      };
      
      setSelectedSalesContract(enhancedContract);
      if (salesContract.closing_date) {
        setContractClosurePopup({
          open: true,
          message: buildClosedContractMessage(salesContract)
        });
      } else {
        setContractClosurePopup({ open: false, message: '' });
      }
      
      // Auto-fill commodity and buyer from sales contract
      setFormData(prev => ({ 
        ...prev, 
        commodity_id: salesContract.commodity_id || '',
        buyer_id: salesContract.buyer_id || '',
        buyer_name: salesContract.buyer_name || ''
      }));
      
      // Fetch buyer balance when buyer is auto-filled from sales contract
      if (salesContract.buyer_id) {
        fetchBuyerBalance(salesContract.buyer_id);
        // Fetch value of undelivered material based on buyer_id
        fetchValueOfUndeliveredMaterial(salesContract.buyer_id);
        fetchEmdLessThan90Value(salesContract.buyer_id);
        // Fetch unapproved received amount buyer-wise
        fetchUnapprovedReceivedAmount(salesContract.buyer_id);
      }
      
      // Validate current quantity if any
      if (formData.quantity_mt) {
        validateQuantity(formData.quantity_mt, enhancedContract);
      } else {
        // Still validate balance if buyer balance is available
        if (buyerBalance !== null && !buyerBalanceLoading) {
          validateBalanceExceeded(formData.quantity_mt || '', enhancedContract);
        }
      }
      
      // If warehouse is already selected, validate warehouse quantity
      if (formData.warehouse_id && salesContract.commodity_id) {
        setTimeout(() => {
          validateWarehouseQuantity(formData.warehouse_id, salesContract.commodity_id, salesContractId);
        }, 500);
      }
    }
  } catch (error) {
    console.error('Error fetching sales contract details:', error);
    setSelectedSalesContract(null);
  }
};

  const isContractClosed = !!selectedSalesContract?.closing_date;


  // Fetch warehouses by CMA
  const fetchWarehousesByCma = async (cmaId) => {
    if (!cmaId) {
      setWarehouses([]);
      return;
    }
    try {
      const warehouseRes = await fetch(`${API_BASE_URL}/api/masters/dropdown-warehouse-list-by-cma?cha_cma_id=${cmaId}`, {
        credentials: 'include'
      });
      if (warehouseRes.ok) {
        const warehouseData = await warehouseRes.json();
        setWarehouses(warehouseData.data || []);
      }
    } catch (error) {
      console.error('Error fetching warehouses:', error);
      setWarehouses([]);
    }
  };

  // Fetch warehouses by CHA
  const fetchWarehousesByCha = async (chaId) => {
    if (!chaId) {
      setChaWarehouses([]);
      return;
    }
    try {
      const warehouseRes = await fetch(`${API_BASE_URL}/api/masters/dropdown-warehouse-list-by-cha?cha_cma_id=${chaId}`, {
        credentials: 'include'
      });
      if (warehouseRes.ok) {
        const warehouseData = await warehouseRes.json();
        setChaWarehouses(warehouseData.data || []);
      }
    } catch (error) {
      console.error('Error fetching CHA warehouses:', error);
      setChaWarehouses([]);
    }
  };

  const getMaxDate = () => new Date().toISOString().split('T')[0]; // YYYY-MM-DD

  const preventDateTyping = (e) => {
    if (e.key !== 'Tab' && e.key !== 'Escape') e.preventDefault();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'do_date' && value) {
      const maxDate = getMaxDate();
      if (value > maxDate) {
        setMessage({ type: 'error', text: 'Future dates are not allowed for DO Date.' });
        return;
      }
    }
    if (name === 'do_date') {
      setMessage(prev => (prev?.type === 'error' && prev?.text === 'Future dates are not allowed for DO Date.' ? null : prev));
    }

    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (name === 'quantity_mt') {
      validateQuantity(value, selectedSalesContract);
    }
    
    // Clear any existing error message when user starts typing
    if (message && message.type === 'error' && message.text.includes('Quantity')) {
      setMessage(null);
    }
    
    // Clear balance error when user starts typing quantity
    if (name === 'quantity_mt' && balanceExceededError) {
      // Validation will be called again in validateQuantity
    }
  };

  const validateQuantity = (quantity, contract = selectedSalesContract) => {
    if (!contract || !quantity) {
      setQuantityError('');
      validateBalanceExceeded(quantity, contract);
      return;
    }

    const quantityNum = parseFloat(parseFloat(quantity || 0).toFixed(3));
    const remainingQtyRaw = parseFloat(contract.remaining_quantity_mt ?? contract.contract_quantity_mt ?? 0);
    const remainingQty = parseFloat(remainingQtyRaw.toFixed(3));
    const roundedWarehouseQty = warehouseQuantity !== null && warehouseQuantity !== undefined ? parseFloat(Number(warehouseQuantity).toFixed(3)) : 0;
    
    if (remainingQty <= 0) {
      setQuantityError('No quantity available for this sales contract. All quantity has been used.');
    } else if (quantityNum > remainingQty) {
      setQuantityError(`Quantity cannot exceed available quantity (${remainingQty.toFixed(3)} MT)`);
    } else if (formData.warehouse_id && roundedWarehouseQty >= 0 && quantityNum > roundedWarehouseQty) {
      setQuantityError(`Quantity cannot exceed available warehouse stock (${roundedWarehouseQty.toFixed(3)} MT)`);
    } else {
      setQuantityError('');
    }
    
    // Also validate balance
    validateBalanceExceeded(quantity, contract);
  };

  // Re-validate quantity if warehouse quantity changes (e.g. user selects warehouse after typing quantity)
  useEffect(() => {
    if (formData.quantity_mt && selectedSalesContract) {
      validateQuantity(formData.quantity_mt, selectedSalesContract);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warehouseQuantity, formData.quantity_mt, selectedSalesContract]);

  // Validate if total amount exceeds buyer balance
  const validateBalanceExceeded = (quantity, contract = selectedSalesContract) => {
  // Use calculatedBuyersBalance instead of buyerBalance
  if (calculatedBuyersBalance === null) {
    setBalanceExceededError('');
    return;
  }

  if (!quantity || !contract || !contract.sale_price_per_mt) {
    setBalanceExceededError('');
    return;
  }

  const quantityNum = parseFloat(quantity);
  const salePricePerMt = parseFloat(contract.sale_price_per_mt || 0);
  const totalAmount = quantityNum * salePricePerMt;
  const balance = parseFloat(calculatedBuyersBalance || 0);

  if (totalAmount > balance) {
    const formattedTotal = new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(totalAmount);
    
    const formattedBalance = new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(balance);

    setBalanceExceededError(
      `Total amount (${formattedTotal}) exceeds buyer balance (${formattedBalance}). Cannot create delivery order.`
    );
  } else {
    setBalanceExceededError('');
  }
};


  // Handle type change (CMA/CHA)
  const handleTypeChange = (type) => {
    setSelectedType(type);
    setFormData(prev => ({ 
      ...prev, 
      cha_cma_id: '', 
      check_cma_cha: type, // Update the check field
      warehouse_id: '', 
      warehouse_location: '' 
    }));
    setWarehouses([]);
    setChaWarehouses([]);
    // Clear warehouse quantity when type changes
    setWarehouseQuantity(0);
    setWarehouseQuantityMessage('');
  };

  const handleDropdownChange = (fieldName, value) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
    
    if (fieldName === 'cha_cma_id') {
      if (selectedType === 'CMA') {
        fetchWarehousesByCma(value);
      } else {
        fetchWarehousesByCha(value);
      }
      setFormData(prev => ({ ...prev, warehouse_id: '', warehouse_location: '' }));
      // Clear warehouse quantity when CMA/CHA changes
      setWarehouseQuantity(0);
      setWarehouseQuantityMessage('');
    }
    
    if (fieldName === 'warehouse_id') {
      const warehouseList = selectedType === 'CMA' ? warehouses : chaWarehouses;
      const selectedWarehouse = warehouseList.find(w => w.warehouse_id === value);
      setFormData(prev => ({
        ...prev,
        warehouse_id: value,
        warehouse_location: selectedWarehouse ? selectedWarehouse.location : ''
      }));
      
      // Validate warehouse quantity when warehouse is selected
      // Check if we have all required fields for validation
      if (value && formData.commodity_id && formData.sales_contract_id) {
        validateWarehouseQuantity(value, formData.commodity_id, formData.sales_contract_id);
      } else if (value) {
        // Show message that other fields are needed
        setWarehouseQuantityMessage('ℹ️ Please select Sales Contract and Commodity to check warehouse quantity');
        setWarehouseQuantity(0);
      } else {
        // Clear message when no warehouse is selected
        setWarehouseQuantityMessage('');
        setWarehouseQuantity(0);
      }
    }
    
    // Handle buyer selection - store both buyer_id and buyer_name
    if (fieldName === 'buyer_id') {
      const selectedBuyer = buyers.find(b => b.domestic_buyer_id === value);
      setFormData(prev => ({
        ...prev,
        buyer_id: value,
        buyer_name: selectedBuyer ? selectedBuyer.name : ''
      }));
      
      // Fetch buyer balance from party ledger
      fetchBuyerBalance(value);
      // Fetch value of undelivered material based on buyer_id
      fetchValueOfUndeliveredMaterial(value);
      fetchEmdLessThan90Value(value);
      // Fetch unapproved received amount buyer-wise
      fetchUnapprovedReceivedAmount(value);
    }
    
    // If sales contract is selected, fetch its details
    if (fieldName === 'sales_contract_id') {
      // Clear warehouse quantity message immediately and forcefully
      setWarehouseQuantity(0);
      setWarehouseQuantityMessage('');
      
      // Clear all form fields when sales contract changes
      if (value) {
        setFormData({
          do_date: '',
          issuing_authority_id: '',
          do_number: '',
          warehouse_id: '',
          warehouse_location: '',
          buyer_id: '',
          buyer_name: '',
          commodity_id: '',
          quantity_mt: '',
          cha_cma_id: '',
          check_cma_cha: 'CMA',
          sales_contract_id: value
        });
        
        // Reset other states
        setSelectedType('CMA');
        setWarehouses([]);
        setChaWarehouses([]);
        setIsContractFullyUtilized(false);
        setQuantityError('');
        setBuyerBalance(null); // Clear buyer balance when sales contract changes
        setValueOfUndeliveredMaterial(null); // Clear value of undelivered material
        setValueOfEmdLessThan90(null); // Clear EMD less than 90 value
        setUnapprovedReceivedAmount(null); // Clear unapproved received amount
        setCalculatedBuyersBalance(null); // Clear calculated buyer's balance
        setBuyersBalanceError(''); // Clear buyer's balance error
        setContractClosurePopup({ open: false, message: '' });
        
        // Force clear warehouse quantity message again
        setTimeout(() => {
          setWarehouseQuantityMessage('');
        }, 100);
      } else {
        // If no sales contract selected, clear buyer balance
        setBuyerBalance(null);
        setValueOfUndeliveredMaterial(null); // Clear value of undelivered material
        setValueOfEmdLessThan90(null); // Clear EMD less than 90 value
        setUnapprovedReceivedAmount(null); // Clear unapproved received amount
        setCalculatedBuyersBalance(null); // Clear calculated buyer's balance
        setBuyersBalanceError(''); // Clear buyer's balance error
      }
      
      fetchSalesContractDetails(value);
    }
    
    // If commodity changes, re-validate warehouse quantity
    if (fieldName === 'commodity_id' && formData.warehouse_id && formData.sales_contract_id) {
      validateWarehouseQuantity(formData.warehouse_id, value, formData.sales_contract_id);
    }
  };

  // Check if balance error exists - disable all fields if true
  const isBalanceError = !!balanceExceededError || !!buyersBalanceError || (calculatedBuyersBalance !== null && calculatedBuyersBalance < 0);

 const handleSubmit = async (e) => {
  e.preventDefault();

  // Check if required fields are filled
  if (!formData.sales_contract_id || !formData.cha_cma_id || !formData.warehouse_id || !formData.quantity_mt || !formData.do_date || !formData.do_number || !formData.issuing_authority_id) {
    setMessage({ type: 'error', text: 'Please fill in all required fields before saving.' });
    return;
  }

  // Check for quantity validation before submitting
  if (quantityError) {
    setMessage({ type: 'error', text: 'Please fix the quantity error before submitting' });
    return;
  }

  // Check if total amount exceeds buyer balance
  if (balanceExceededError) {
    setMessage({ type: 'error', text: balanceExceededError });
    return;
  }

  // Check if Buyer's Balance is negative
  if (buyersBalanceError || (calculatedBuyersBalance !== null && calculatedBuyersBalance < 0)) {
    const formattedBalance = calculatedBuyersBalance !== null 
      ? new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(calculatedBuyersBalance)
      : 'N/A';
    setMessage({ 
      type: 'error', 
      text: `Buyer's Balance is negative (${formattedBalance}). Cannot create delivery order.`
    });
    return;
  }

  if (!emdStatus.allowed) {
    setMessage({
      type: 'error',
      text: emdStatus.message || 'Party is having pending EMD.'
    });
    return;
  }

  if (isContractClosed) {
    const closedContractMessage = buildClosedContractMessage(selectedSalesContract);
    setContractClosurePopup({ open: true, message: closedContractMessage });
    setMessage({ type: 'error', text: closedContractMessage });
    return;
  }

  // Check if warehouse has no quantity available
  if (warehouseQuantity <= 0 && formData.warehouse_id) {
    setMessage({ 
      type: 'error', 
      text: 'No quantity available in selected warehouse. Please create a warehouse entry first or select a different warehouse.'
    });
    return;
  }

  // Check if requested quantity exceeds available warehouse stock
  const submitQty = parseFloat(parseFloat(formData.quantity_mt || 0).toFixed(3));
  const submitWhQty = parseFloat(Number(warehouseQuantity).toFixed(3));
  if (formData.warehouse_id && formData.quantity_mt && submitQty > submitWhQty) {
    setMessage({ 
      type: 'error', 
      text: `Delivery quantity (${formData.quantity_mt} MT) cannot exceed available warehouse stock (${warehouseQuantity.toFixed(3)} MT).`
    });
    return;
  }

  const maxDate = getMaxDate();
  if (formData.do_date && formData.do_date > maxDate) {
    setMessage({ type: 'error', text: 'Future dates are not allowed for DO Date.' });
    return;
  }

  setLoading(true);
  setMessage(null);

  try {
    const res = await fetch(`${API_BASE_URL}/api/sales/delivery-order-post`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(formData),
    });

    const data = await res.json();

    if (res.ok) {
      setMessage({ type: 'success', text: 'Delivery Order Created Successfully!' });
      
      // Reset form
      setFormData({
        do_date: '',
        issuing_authority_id: '',
        do_number: '',
        warehouse_id: '',
        warehouse_location: '',
        buyer_id: '',
        buyer_name: '',
        commodity_id: '',
        quantity_mt: '',
        cha_cma_id: '',
        check_cma_cha: 'CMA',
        sales_contract_id: ''
      });
      
      setSelectedSalesContract(null);
      setQuantityError('');
      setSelectedType('CMA');
      setWarehouseQuantity(0);
      setWarehouseQuantityMessage('');
      setIsContractFullyUtilized(false);
      setBuyerBalance(null);
      setBalanceExceededError('');
      setValueOfUndeliveredMaterial(null);
      setUnapprovedReceivedAmount(null);
      setCalculatedBuyersBalance(null);
      setBuyersBalanceError('');
      setContractClosurePopup({ open: false, message: '' });
    } else {
      setMessage({ type: 'error', text: data.error || 'Something went wrong' });
    }
  } catch (err) {
    setMessage({ type: 'error', text: 'Server Error' });
  }

  setLoading(false);
};


  return (
    <div className="max-w-8xl mx-auto p-0 bg-white">
      {contractClosurePopup.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-md rounded bg-white shadow-xl">
            <div className="border-b border-gray-200 px-4 py-3">
              <h3 className="text-base font-semibold text-gray-900">Sales Contract Closed</h3>
            </div>
            <div className="px-4 py-4">
              <p className="text-sm leading-6 text-gray-700">{contractClosurePopup.message}</p>
            </div>
            <div className="flex justify-end border-t border-gray-200 px-4 py-3">
              <button
                type="button"
                onClick={() => setContractClosurePopup(prev => ({ ...prev, open: false }))}
                className="bg-[#3899cf] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0e8dc6]"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="flex justify-between items-center border-b border-gray-100 bg-gray-50 px-4 py-1.5">
        <h2 className="text-lg font-bold mb-0 text-left">Delivery Order Entry</h2>
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-[#3899cf] border border-[#10a5e9] px-2 py-0.5 rounded text-xs font-medium hover:bg-[#eaf6fd]"
        >
          <FaArrowLeft />
          Back
        </button>
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-0">
        <div className="px-4 pt-4 pb-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* 1. Sales Contract No */}
            <div>
              <label className="block text-xs font-medium mb-1">Sales Contract No</label>
              <SearchableDropdown
                options={salesContracts}
                value={formData.sales_contract_id}
                onChange={val => handleDropdownChange('sales_contract_id', val)}
                placeholder="Select Sales Contract"
                displayKey="sales_contract_no"
                valueKey="sales_contract_id"
                required
              />
              {/* Contract Details Display */}
              {selectedSalesContract && (
                <div className="mt-2 p-2 bg-gray-50 rounded-sm border border-gray-200">
                  <div className="text-xs text-gray-700 space-y-1">
                    {/* Sales Contract Quantity */}
                    <div className="flex justify-between">
                      <span>Sales Contract Quantity:</span>
                      <span className="font-medium text-blue-600">
                        {parseFloat(selectedSalesContract.contract_quantity_mt || 0).toLocaleString('en-IN', {
                          minimumFractionDigits: 3,
                          maximumFractionDigits: 3
                        })} MT
                      </span>
                    </div>
                    
                    {/* Selected Warehouse Quantity */}
                    {formData.warehouse_id && (
                      <div className="border-t border-gray-300 pt-1 mt-1">
                        <div className="font-semibold text-gray-800 mb-1">Selected Warehouse Stock:</div>
                        <div className="flex justify-between pl-2">
                          <span>Available in Warehouse:</span>
                          <span className={`font-medium ${
                            warehouseQuantity > 0 ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {warehouseQuantity.toLocaleString('en-IN', {
                              minimumFractionDigits: 3,
                              maximumFractionDigits: 3
                            })} MT
                          </span>
                        </div>
                        {warehouseQuantityMessage && (
                          <div className={`pl-2 text-xs ${
                            warehouseQuantityMessage.includes('⚠️') ? 'text-red-600' : 'text-green-600'
                          }`}>
                            {warehouseQuantityMessage.replace('✅ Available quantity: ', '').replace(' (Received: ', ' (Received: ')}
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Used in Delivery Orders */}
                    <div className="flex justify-between border-t border-gray-300 pt-1 mt-1">
                      <span>Used in Delivery Orders:</span>
                      <span className="font-medium text-gray-600">
                        {parseFloat(selectedSalesContract.used_quantity_mt || 0).toLocaleString('en-IN', {
                          minimumFractionDigits: 3,
                          maximumFractionDigits: 3
                        })} MT
                      </span>
                    </div>
                    
                    {/* Available for Delivery */}
                    <div className="flex justify-between">
                      <span>Available for Delivery:</span>
                      <span className={`font-medium ${
                        parseFloat(selectedSalesContract.remaining_quantity_mt ?? selectedSalesContract.contract_quantity_mt ?? 0) <= 0 
                          ? 'text-red-600' 
                          : 'text-green-600'
                      }`}>
                        {parseFloat(selectedSalesContract.remaining_quantity_mt ?? selectedSalesContract.contract_quantity_mt ?? 0).toLocaleString('en-IN', {
                          minimumFractionDigits: 3,
                          maximumFractionDigits: 3
                        })} MT
                      </span>
                    </div>
                    
                    {parseFloat(selectedSalesContract.remaining_quantity_mt ?? selectedSalesContract.contract_quantity_mt ?? 0) <= 0 && (
                      <div className="text-red-600 font-medium text-xs mt-1">
                        ⚠️ No quantity available for this contract
                      </div>
                    )}
                    
                    {/* Check if contract is fully utilized */}
                    {selectedSalesContract && parseFloat(selectedSalesContract.contract_quantity_mt || 0) === parseFloat(selectedSalesContract.used_quantity_mt || 0) && parseFloat(selectedSalesContract.contract_quantity_mt || 0) > 0 && (
                      <div className="text-red-600 font-medium text-xs mt-1">
                        🚫 Sales contract fully utilized - No more delivery orders allowed
                      </div>
                    )}
                    {isContractClosed && (
                      <div className="text-red-600 font-medium text-xs mt-1">
                        Sales contract is closed. Delivery order creation is not allowed.
                      </div>
                    )}
                  </div>
                </div>
              )}
              {!emdStatus.allowed && emdStatus.message && (
                <p style={{
                  color: '#92400e',
                  fontSize: '13px',
                  fontWeight: '600',
                  marginTop: '6px',
                  background: '#fef3c7',
                  border: '1px solid #fcd34d',
                  borderRadius: '4px',
                  padding: '6px 10px',
                  display: 'inline-flex',      // ← flex for icon alignment
                  alignItems: 'center',
                  gap: '5px',
                }}>
                  <MdWarning size={15} color="#d97706" />  {/* ✅ React Icon */}
                  {emdStatus.message}
                </p>
              )}
              {emdStatus.checking && (
                <div className="text-xs text-gray-500 mt-1">
                  Checking pending EMD status for this party...
                </div>
              )}
            </div>
            
            {/* 2. Commodity */}
            <div>
              <label className="block text-xs font-medium mb-1">Commodity</label>
              <SearchableDropdown
                options={commodities}
                value={formData.commodity_id}
                onChange={val => handleDropdownChange('commodity_id', val)}
                placeholder={selectedSalesContract ? "Auto-selected from Sales Contract" : "Select Commodity"}
                displayKey="name"
                valueKey="commodity_id"
                required
                customDisplay={(item) => `${item.name} (${item.origin || 'N/A'})`}
                disabled={isEmdBlocked || !!selectedSalesContract || isContractFullyUtilized || isBalanceError || isContractClosed} // Disable when sales contract is selected or contract is fully utilized or balance error
              />
            </div>

            {/* 3. CMA/CHA Checkbox Section */}
            <div className="md:col-span-2 flex gap-6 items-center mb-2">
              <label className="flex items-center gap-1">
                <input 
                  type="checkbox" 
                  checked={selectedType === 'CMA'} 
                  onChange={() => handleTypeChange('CMA')} 
                  disabled={isEmdBlocked || isContractFullyUtilized || isBalanceError || isContractClosed}
                /> 
                CMA
              </label>
              <label className="flex items-center gap-1">
                <input 
                  type="checkbox" 
                  checked={selectedType === 'CHA'} 
                  onChange={() => handleTypeChange('CHA')} 
                  disabled={isEmdBlocked || isContractFullyUtilized || isBalanceError || isContractClosed}
                /> 
                CHA
              </label>
            </div>

            {/* 4. Conditional CMA Dropdown */}
            {selectedType === 'CMA' && (
              <div>
                <label className="block text-xs font-medium mb-1">CMA</label>
                <SearchableDropdown
                  options={cmas}
                  value={formData.cha_cma_id}
                  onChange={val => handleDropdownChange('cha_cma_id', val)}
                  placeholder="Select CMA"
                  displayKey="name"
                  valueKey="cha_cma_id"
                  required
                  disabled={isEmdBlocked || isContractFullyUtilized || isBalanceError || isContractClosed}
                />
              </div>
            )}

            {/* 4. Conditional CHA Dropdown */}
            {selectedType === 'CHA' && (
              <div>
                <label className="block text-xs font-medium mb-1">CHA</label>
                <SearchableDropdown
                  options={chas}
                  value={formData.cha_cma_id}
                  onChange={val => handleDropdownChange('cha_cma_id', val)}
                  placeholder="Select CHA"
                  displayKey="name"
                  valueKey="cha_cma_id"
                  required
                  disabled={isEmdBlocked || isContractFullyUtilized || isBalanceError || isContractClosed}
                />
              </div>
            )}

            {/* 5. Conditional Warehouse Dropdown */}
            {selectedType === 'CMA' && (
              <div>
                <label className="block text-xs font-medium mb-1">Warehouse</label>
                <SearchableDropdown
                  options={warehouses.map(w => ({ ...w, name: `${w.name} (${w.warehouse_location})` }))}
                  value={formData.warehouse_id}
                  onChange={val => handleDropdownChange('warehouse_id', val)}
                  placeholder="Select Warehouse"
                  displayKey="name"
                  valueKey="warehouse_id"
                  required
                  disabled={isEmdBlocked || isContractFullyUtilized || isBalanceError || isContractClosed}
                />
              </div>
            )}

            {selectedType === 'CHA' && (
              <div>
                <label className="block text-xs font-medium mb-1">Warehouse/Port</label>
                <SearchableDropdown
                  options={chaWarehouses.map(w => ({ 
                    ...w, 
                    name: `${w.name || w.warehouse_name || w.port_name} ${w.location ? `(${w.location})` : ''}` 
                  }))}
                  value={formData.warehouse_id}
                  onChange={val => handleDropdownChange('warehouse_id', val)}
                  placeholder="Select Warehouse/Port"
                  displayKey="name"
                  valueKey="warehouse_id"
                  required
                  disabled={isEmdBlocked || isContractFullyUtilized || isBalanceError || isContractClosed}
                />
              </div>
            )}

            {/* 6. Location */}
            <div>
              <label className="block text-xs font-medium mb-1">Location</label>
              <input
                type="text"
                name="warehouse_location"
                value={formData.warehouse_location}
                onChange={handleChange}
                className={`w-full border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:border-[#10a5e9] rounded-none ${
                  (isContractFullyUtilized || isBalanceError || isContractClosed) ? 'bg-gray-100' : 'bg-white'
                }`}
                required
                readOnly
                disabled={isEmdBlocked || isContractFullyUtilized || isBalanceError || isContractClosed}
              />
              {/* Warehouse Quantity Message - Only show when warehouse is selected */}
              {warehouseQuantityMessage && formData.warehouse_id && (
                <div className={`mt-1 text-xs ${
                  warehouseQuantityMessage.includes('⚠️') ? 'text-red-600' : 'text-green-600'
                }`}>
                  {warehouseQuantityMessage}
                </div>
              )}
            </div>
            
            {/* 7. Buyer Name */}
            <div>
              <label className="block text-xs font-medium mb-1">Buyer Name</label>
              <SearchableDropdown
                options={buyers}
                value={formData.buyer_id}
                onChange={val => handleDropdownChange('buyer_id', val)}
                placeholder={selectedSalesContract ? "Auto-selected from Sales Contract" : "Select Buyer"}
                displayKey="name"
                valueKey="domestic_buyer_id"
                required
                customDisplay={(item) => `${item.name}${item.state ? `, ${item.state}` : ''}`}
                disabled={isEmdBlocked || !!selectedSalesContract || isContractFullyUtilized || isBalanceError || isContractClosed} // Disable when sales contract is selected or contract is fully utilized or balance error
              />
            </div>
            
            {/* 7.1. Ledger Balance */}
            <div>
              <label className="block text-xs font-medium mb-1">Ledger Balance (₹)</label>
              <input
                type="text"
                name="buyer_balance"
                value={
                  buyerBalanceLoading 
                    ? 'Loading...' 
                    : buyerBalance !== null 
                      ? new Intl.NumberFormat('en-IN', {
                          style: 'currency',
                          currency: 'INR',
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        }).format(buyerBalance)
                      : ''
                }
                readOnly
                className={`w-full border border-gray-300 px-3 py-1.5 text-sm rounded-none cursor-not-allowed ${
                  buyerBalance !== null 
                    ? buyerBalance < 0 
                      ? 'bg-red-50 text-red-700' 
                      : 'bg-gray-100'
                    : 'bg-gray-100'
                }`}
                placeholder={formData.buyer_id ? "Fetching balance..." : "Select buyer to see balance"}
                disabled
              />
            </div>
            
            {/* 8. Sale Price Per MT */}
            <div>
              <label className="block text-xs font-medium mb-1">Sale Price Per MT (₹)</label>
              <input
                type="text"
                name="sale_price_per_mt"
                value={selectedSalesContract?.sale_price_per_mt ? parseFloat(selectedSalesContract.sale_price_per_mt).toLocaleString('en-IN', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                }) : ''}
                readOnly
                className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-gray-100 rounded-none cursor-not-allowed"
                placeholder="Auto-populated from Sales Contract"
                disabled
              />
            </div>
            
            {/* 8.1. Value of Undelivered Material */}
            <div className="md:col-span-1">
              <label className="block text-xs font-medium mb-1">Value of Undelivered Material (₹)</label>
              <input
                type="text"
                name="value_of_undelivered_material"
                value={
                  valueOfUndeliveredMaterialLoading 
                    ? 'Loading...' 
                    : valueOfUndeliveredMaterial !== null 
                      ? new Intl.NumberFormat('en-IN', {
                          style: 'currency',
                          currency: 'INR',
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        }).format(valueOfUndeliveredMaterial)
                      : ''
                }
                readOnly
                className={`w-full border border-gray-300 px-3 py-1.5 text-sm rounded-none cursor-not-allowed ${
                  valueOfUndeliveredMaterial !== null 
                    ? valueOfUndeliveredMaterial < 0 
                      ? 'bg-red-50 text-red-700' 
                      : 'bg-gray-100'
                    : 'bg-gray-100'
                }`}
                placeholder={formData.buyer_id ? "Fetching value..." : "Select buyer to see value"}
                disabled
              />
              
              {/* Breakdown Table */}
              {/* {valueOfUndeliveredMaterialBreakdown.length > 0 && !valueOfUndeliveredMaterialLoading && (
                <div className="mt-3 border border-gray-300 rounded-sm overflow-hidden">
                  <div className="bg-gray-50 px-3 py-2 border-b border-gray-300">
                    <h4 className="text-xs font-semibold text-gray-700">Calculation Breakdown</h4>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="px-3 py-2 text-left border-b border-gray-300 font-semibold text-gray-700">DO Number</th>
                          <th className="px-3 py-2 text-right border-b border-gray-300 font-semibold text-gray-700">DO Qty (MT)</th>
                          <th className="px-3 py-2 text-right border-b border-gray-300 font-semibold text-gray-700">SO Qty (MT)</th>
                          <th className="px-3 py-2 text-right border-b border-gray-300 font-semibold text-gray-700">Balance Qty (MT)</th>
                          <th className="px-3 py-2 text-right border-b border-gray-300 font-semibold text-gray-700">Rate (₹)</th>
                          <th className="px-3 py-2 text-right border-b border-gray-300 font-semibold text-gray-700">Value (₹)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {valueOfUndeliveredMaterialBreakdown.map((item, index) => (
                          <tr key={item.do_id || index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                            <td className="px-3 py-2 border-b border-gray-200 text-gray-800">{item.do_number || 'N/A'}</td>
                            <td className="px-3 py-2 border-b border-gray-200 text-right text-gray-700">
                              {item.do_qty.toLocaleString('en-IN', {
                                minimumFractionDigits: 3,
                                maximumFractionDigits: 3
                              })}
                            </td>
                            <td className="px-3 py-2 border-b border-gray-200 text-right text-gray-700">
                              {item.so_qty.toLocaleString('en-IN', {
                                minimumFractionDigits: 3,
                                maximumFractionDigits: 3
                              })}
                            </td>
                            <td className="px-3 py-2 border-b border-gray-200 text-right font-medium text-gray-800">
                              {item.balance_qty.toLocaleString('en-IN', {
                                minimumFractionDigits: 3,
                                maximumFractionDigits: 3
                              })}
                            </td>
                            <td className="px-3 py-2 border-b border-gray-200 text-right text-gray-700">
                              {item.rate.toLocaleString('en-IN', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                              })}
                            </td>
                            <td className="px-3 py-2 border-b border-gray-200 text-right font-semibold text-blue-600">
                              {item.value.toLocaleString('en-IN', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                              })}
                            </td>
                          </tr>
                        ))}
                        <tr className="bg-blue-50 font-semibold">
                          <td colSpan="5" className="px-3 py-2 border-t-2 border-gray-400 text-right text-gray-800">
                            Total Value of Undelivered Material:
                          </td>
                          <td className="px-3 py-2 border-t-2 border-gray-400 text-right text-blue-700">
                            {valueOfUndeliveredMaterial !== null 
                              ? new Intl.NumberFormat('en-IN', {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2
                                }).format(valueOfUndeliveredMaterial)
                              : '0.00'}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )} */}
            </div>

            {/* 8.1.1 Value of EMD of Contracts executed less than 90% */}
            <div className="md:col-span-1 relative">
              <label className="block text-xs font-medium mb-1">
                Value of EMD of Contracts executed less than 90% (₹)
              </label>
              <input
                type="text"
                name="value_of_emd_less_than_90"
                value={
                  valueOfEmdLessThan90Loading 
                    ? 'Loading...' 
                    : valueOfEmdLessThan90 !== null 
                      ? new Intl.NumberFormat('en-IN', {
                          style: 'currency',
                          currency: 'INR',
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        }).format(valueOfEmdLessThan90)
                      : ''
                }
                readOnly
                className={`w-full border border-gray-300 px-3 py-1.5 text-sm bg-gray-100 rounded-none text-green-700 font-medium ${emdLessThan90Details.length > 0 ? 'cursor-pointer hover:bg-gray-200' : 'cursor-not-allowed'}`}
                placeholder={formData.buyer_id ? "Fetching value..." : "Select buyer to see value"}
                disabled={emdLessThan90Details.length === 0}
                onClick={() => { if(emdLessThan90Details.length > 0) setShowEmdDetails(!showEmdDetails); }}
              />
              {showEmdDetails && emdLessThan90Details.length > 0 && (
                <div className="absolute z-50 mt-1 w-[400px] bg-white border border-gray-300 shadow-lg p-3 text-xs text-gray-700 max-h-60 overflow-y-auto rounded-md">
                  <div className="flex justify-between items-center mb-2 border-b pb-1">
                    <span className="font-bold text-sm">EMD Value Breakdown</span>
                    <button 
                      onClick={() => setShowEmdDetails(false)} 
                      className="text-gray-500 hover:text-red-500 font-bold"
                      type="button"
                    >
                      &times;
                    </button>
                  </div>
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="p-1 border">Contract No</th>
                        <th className="p-1 border text-right">Qty (MT)</th>
                        <th className="p-1 border text-right">Price (₹)</th>
                        <th className="p-1 border text-right">EMD%</th>
                        <th className="p-1 border text-right">Value (₹)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {emdLessThan90Details.map((detail, idx) => (
                        <tr key={idx} className="border-b hover:bg-gray-50">
                          <td className="p-1 border font-medium">{detail.sales_contract_no}</td>
                          <td className="p-1 border text-right">{detail.contract_quantity_mt}</td>
                          <td className="p-1 border text-right">{detail.sale_price_per_mt}</td>
                          <td className="p-1 border text-right">{detail.emd}%</td>
                          <td className="p-1 border text-right font-semibold text-green-700">
                            {new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(detail.emd_value)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            
            {/* 8.2. Unapproved Received Amount */}
            <div>
              <label className="block text-xs font-medium mb-1">Unapproved Received Amount (₹)</label>
              <input
                type="text"
                name="unapproved_received_amount"
                value={
                  unapprovedReceivedAmountLoading 
                    ? 'Loading...' 
                    : unapprovedReceivedAmount !== null 
                      ? new Intl.NumberFormat('en-IN', {
                          style: 'currency',
                          currency: 'INR',
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        }).format(unapprovedReceivedAmount)
                      : ''
                }
                readOnly
                className={`w-full border border-gray-300 px-3 py-1.5 text-sm rounded-none cursor-not-allowed ${
                  unapprovedReceivedAmount !== null 
                    ? unapprovedReceivedAmount > 0 
                      ? 'bg-yellow-50 text-yellow-700' 
                      : 'bg-gray-100'
                    : 'bg-gray-100'
                }`}
                placeholder={formData.buyer_id ? "Fetching amount..." : "Select buyer to see amount"}
                disabled
              />
            </div>
            
            {/* 8.3. Buyer's Balance (Calculated) */}
            <div>
              <label className="block text-xs font-medium mb-1">Buyer's Balance (₹)</label>
              <input
                type="text"
                name="calculated_buyers_balance"
                value={
                  calculatedBuyersBalance !== null 
                    ? new Intl.NumberFormat('en-IN', {
                        style: 'currency',
                        currency: 'INR',
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      }).format(calculatedBuyersBalance)
                    : ''
                }
                readOnly
                className={`w-full border px-3 py-1.5 text-sm rounded-none cursor-not-allowed ${
                  calculatedBuyersBalance !== null 
                    ? calculatedBuyersBalance < 0 
                      ? 'bg-red-50 text-red-700 border-red-500' 
                      : 'bg-gray-100'
                    : 'bg-gray-100'
                }`}
                placeholder="Auto-calculated: (Ledger + Unapproved) - (Undelivered + EMD<90%) - (Qty × Price)"
                disabled
              />
              {buyersBalanceError && (
                <div className="text-red-600 text-xs mt-1 font-semibold">{buyersBalanceError}</div>
              )}
            </div>
            
            {/* 9. Quantity */}
            <div>
              <label className="block text-xs font-medium mb-1">
                Quantity (MT)
                {selectedSalesContract && (
                  <span className="text-gray-500 ml-1">
                    (Max: {parseFloat(selectedSalesContract.remaining_quantity_mt ?? selectedSalesContract.contract_quantity_mt ?? 0).toLocaleString('en-IN', {
                      minimumFractionDigits: 3,
                      maximumFractionDigits: 3
                    })} MT)
                  </span>
                )}
              </label>
              <input
                type="number"
                name="quantity_mt"
                value={formData.quantity_mt}
                onChange={handleChange}
                className={`w-full border px-3 py-1.5 text-sm focus:outline-none rounded-none ${
                  (quantityError || balanceExceededError) ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-[#10a5e9]'
                } ${(isContractFullyUtilized || isContractClosed) ? 'bg-gray-100' : 'bg-white'}`}
                step="0.001"
                max={selectedSalesContract ? (selectedSalesContract.remaining_quantity_mt ?? selectedSalesContract.contract_quantity_mt ?? 0) : undefined}
                required
                disabled={isEmdBlocked || isContractFullyUtilized || isContractClosed}
              />
              {quantityError && (
                <div className="text-red-500 text-xs mt-1">{quantityError}</div>
              )}
              {balanceExceededError && (
                <div className="text-red-600 text-xs mt-1 font-semibold">{balanceExceededError}</div>
              )}
            </div>
            
            {/* 10. Other fields */}
            <div>
              <label className="block text-xs font-medium mb-1">DO Date</label>
              <input
                type="date"
                name="do_date"
                value={formData.do_date}
                onChange={handleChange}
                onKeyDown={preventDateTyping}
                max={getMaxDate()}
                className={`w-full border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:border-[#10a5e9] rounded-none cursor-pointer ${
                  (isContractFullyUtilized || isBalanceError || isContractClosed || !!quantityError) ? 'bg-gray-100' : 'bg-white'
                }`}
                title="Select date from calendar only (future dates not allowed)"
                required
                disabled={isEmdBlocked || isContractFullyUtilized || isBalanceError || isContractClosed || !!quantityError}
              />
            </div>
            
            <div>
              <label className="block text-xs font-medium mb-1">DO Number</label>
              <input
                type="text"
                name="do_number"
                value={formData.do_number}
                onChange={handleChange}
                className={`w-full border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:border-[#10a5e9] rounded-none ${
                  (isContractFullyUtilized || isBalanceError || isContractClosed || !!quantityError) ? 'bg-gray-100' : 'bg-white'
                }`}
                required
                disabled={isEmdBlocked || isContractFullyUtilized || isBalanceError || isContractClosed || !!quantityError}
              />
            </div>
            
            <div>
              <label className="block text-xs font-medium mb-1">Issuing Authority</label>
              <SearchableDropdown
                options={issuingAuthorities}
                value={formData.issuing_authority_id}
                onChange={val => handleDropdownChange('issuing_authority_id', val)}
                placeholder="Select Issuing Authority"
                displayKey="authority_name"
                valueKey="id"
                required
                disabled={isEmdBlocked || isContractFullyUtilized || isBalanceError || isContractClosed || !!quantityError}
              />
            </div>
          </div>
        </div>
        
        <div className="px-4 pb-4">
          <button
            type="submit"
            className="bg-[#3899cf] text-white px-6 py-2 rounded hover:bg-[#0e8dc6] font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[#3899cf]"
            disabled={loading || 
              emdStatus.checking ||
              !emdStatus.allowed ||
              !!quantityError ||
              !!balanceExceededError ||
              !!buyersBalanceError ||
              isContractClosed ||
              (calculatedBuyersBalance !== null && calculatedBuyersBalance < 0) ||
              (selectedSalesContract && parseFloat(selectedSalesContract.remaining_quantity_mt ?? selectedSalesContract.contract_quantity_mt ?? 0) <= 0) ||
              (selectedSalesContract && parseFloat(selectedSalesContract.contract_quantity_mt || 0) === parseFloat(selectedSalesContract.used_quantity_mt || 0) && parseFloat(selectedSalesContract.contract_quantity_mt || 0) > 0) ||
              (warehouseQuantity <= 0 && formData.warehouse_id)
            }
          >
            {loading ? 'Saving...' : 'Save'}
          </button>
          {message && (
            <span className={`ml-4 text-sm ${message.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>{message.text}</span>
          )}
        </div>
      </form>
    </div>
  );
};

export default DeliveryOrderForm;
